import React, { useEffect, useState } from 'react';
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faList, faBars, faMap } from '@fortawesome/free-solid-svg-icons';

const GridIcon = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    viewBox="0 0 24 24"
    width="22"
    height="22"
    fill="currentColor"
    style={{ display: 'block', width: '100%', height: '100%' }}
  >
    <rect x="3" y="3" width="8" height="8" rx="2" />
    <rect x="13" y="3" width="8" height="8" rx="2" />
    <rect x="3" y="13" width="8" height="8" rx="2" />
    <rect x="13" y="13" width="8" height="8" rx="2" />
  </svg>
);

interface ViewModeSelectorProps {
  viewMode: string;
  setViewMode: (viewMode: string) => void;
  getThemes: { color1: string, color2: string };
}

const isWhiteOrShade = (color: string): boolean => {
  const s = color.trim().toLowerCase();

  // 1) explicit white checks
  if (
    s === "white" ||
    s === "#fff" ||
    s === "#ffffff" ||
    s === "rgb(255,255,255)" ||
    s === "rgba(255,255,255,1)"
  ) {
    return true;
  }

  let r: number, g: number, b: number;

  // 2) hex formats
  if (s.startsWith("#")) {
    let hex = s.slice(1);
    if (hex.length === 3) {
      // expand `#abc` → `#aabbcc`
      hex = hex.split("").map(ch => ch + ch).join("");
    }
    if (hex.length !== 6 || !/^[0-9a-f]{6}$/i.test(hex)) {
      return false;
    }
    r = parseInt(hex.slice(0, 2), 16);
    g = parseInt(hex.slice(2, 4), 16);
    b = parseInt(hex.slice(4, 6), 16);
  }
  // 3) rgb/rgba formats
  else {
    const m = s.match(
      /rgba?\(\s*(\d{1,3})\s*,\s*(\d{1,3})\s*,\s*(\d{1,3})(?:\s*,\s*[\d.]+)?\s*\)/
    );
    if (!m) return false;
    r = Number(m[1]);
    g = Number(m[2]);
    b = Number(m[3]);
  }

  // 4) threshold for "almost white"
  return r >= 235 && g >= 235 && b >= 235;
};

const ViewModeSelector: React.FC<ViewModeSelectorProps> = ({ viewMode, setViewMode, getThemes }) => {
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    const checkScreenSize = () => {
      setIsMobile(window.innerWidth <= 768);
    };

    checkScreenSize();
    window.addEventListener('resize', checkScreenSize);

    return () => {
      window.removeEventListener('resize', checkScreenSize);
    };
  }, []);

  if (isMobile) {
    return null;
  }

  const defaultBackgroundColor = 'rgba(229, 231, 235, 1)';
  const selectedBackgroundColor = getThemes && getThemes.color2;
  const isSelectedColorWhite = selectedBackgroundColor ? isWhiteOrShade(selectedBackgroundColor) : false;

  // Change background to black and text to white when theme color is white or close to white
  const selectedButtonStyle = {
    padding: '8px',
    width: '36px',
    height: '36px',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    borderRadius: '4px',
    background: isSelectedColorWhite ? 'black' : selectedBackgroundColor,
    color: isSelectedColorWhite ? 'white' : (isWhiteOrShade(selectedBackgroundColor) ? 'black' : 'white'),
  };

  const buttonStyle = {
    padding: '8px',
    width: '36px',
    height: '36px',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    borderRadius: '4px',
    background: defaultBackgroundColor,
    color: 'black',
  };

  return (
    <div style={{ display: 'flex', gap: '8px', paddingRight: '2px' }}>
      <button
        onClick={() => setViewMode('detail')}
        style={viewMode === 'detail' ? selectedButtonStyle : buttonStyle}
      >
        <FontAwesomeIcon 
          icon={faBars} 
          style={{ 
            fontSize: '20px', 
            color: viewMode === 'detail' ? (isSelectedColorWhite ? 'white' : (isWhiteOrShade(selectedBackgroundColor) ? 'black' : 'white')) : 'black' 
          }} 
        />
      </button>
      <button
        onClick={() => setViewMode('grid')}
        style={viewMode === 'grid' ? selectedButtonStyle : buttonStyle}
      >
        <div style={{ 
          color: viewMode === 'grid' ? (isSelectedColorWhite ? 'white' : (isWhiteOrShade(selectedBackgroundColor) ? 'black' : 'white')) : 'black' 
        }}>
          <GridIcon />
        </div>
      </button>
      <button
        onClick={() => setViewMode('list')}
        style={viewMode === 'list' ? selectedButtonStyle : buttonStyle}
      >
        <FontAwesomeIcon 
          icon={faList} 
          style={{ 
            fontSize: '20px', 
            color: viewMode === 'list' ? (isSelectedColorWhite ? 'white' : (isWhiteOrShade(selectedBackgroundColor) ? 'black' : 'white')) : 'black' 
          }} 
        />
      </button>
      <button
        onClick={() => setViewMode('map')}
        style={viewMode === 'map' ? selectedButtonStyle : buttonStyle}
      >
        <FontAwesomeIcon 
          icon={faMap} 
          style={{ 
            fontSize: '20px', 
            color: viewMode === 'map' ? (isSelectedColorWhite ? 'white' : (isWhiteOrShade(selectedBackgroundColor) ? 'black' : 'white')) : 'black' 
          }} 
        />
      </button>
    </div>
  );
};

export default ViewModeSelector;